import * as React from "react";
import {useEffect} from "react";
import {useTranslation} from "react-i18next";
import Breadcrumb from "./Breadcrumb";
import styles from "../lib/ModalWindow/CartWrapperModal.module.scss";
import {useCheckoutViewContext} from "./CheckoutContextProvider/CheckoutViewContextProvider";
import Finished from "./steps/Finished/Finished";
import Login from "./steps/Login/Login";
import Payment from "./steps/Payment/Payment";
import Shipping from "./steps/Shipping/Shipping";
import Summary from "./steps/Summary/Summary";
import {ICustomerData, IProductTrackingData, IPurchaseSummaryInformation, Tracking} from "../../tracking/tracking";
import {CartPosition} from "@connox/typescript-library";
import {useCheckoutContext} from "./CheckoutContextProvider/CheckoutContextProvider";
import AddressStep from "./steps/AddressStep/AddressStep";
import window from "../lib/WindowExtended";
import {useCartContext} from "../cart/CartContextProvider/CartContextProvider";
import {BsArrowLeft as ArrowLeft} from "react-icons/bs";
import {updateUrlParams} from "../cart/CartIcon/CartIconPortal";

const Checkout: React.FC = () => {
  const {t} = useTranslation("checkout");
  const {step} = useCheckoutViewContext();
  const {checkout} = useCheckoutContext();
  const {setIsIntoCheckout} = useCartContext();

  useEffect(() => {
    const productData: IProductTrackingData[] = [];

    checkout.cartData.positions.forEach((position: CartPosition) => {
      if (position.type === "Item" && position.item?.itemId) {
        productData.push({
          brand: position.item.manufacturer,
          id: position.item.itemId,
          name: position.text,
          price: position.price.amount,
          quantity: position.quantity,
        });
      }
    });

    if (step !== CheckoutStep.FINISHED) {
      Tracking.sendCheckoutStepEvent(step, productData);
    } else if (step === CheckoutStep.FINISHED && checkout.orderId) {
      const shippingCosts = checkout.cartData.positions.find<CartPosition>(
        (pos): pos is CartPosition => pos.type === "Shipping",
      )?.amount.amount;

      const voucherCode = checkout.cartData.positions.find<CartPosition>(
        (pos): pos is CartPosition => pos.type === "Voucher" && !!pos.reference?.voucher?.isValid,
      )?.reference?.voucher?.code;

      const promotionId = checkout.cartData.promotion?.id ?? -1;

      const summaryData: IPurchaseSummaryInformation = {
        id: checkout.orderId,
        affiliation: promotionId,
        coupon: voucherCode ?? "",
        revenue: checkout.cartData.value.gross.amount,
        shipping: shippingCosts ?? "0",
        tax: checkout.cartData.value.tax
          .reduce((accumulator, currentTax) => accumulator + parseFloat(currentTax.amount.amount), 0.0)
          .toFixed(2),
      };

      const customerData: ICustomerData = {
        Birthday: checkout.invoiceAddress?.dateOfBirth ?? "",
        Email: checkout.invoiceAddress?.email ?? "",
        Salutation: checkout.invoiceAddress?.gender === "F" ? "Mrs." : "Mr.",
      };

      Tracking.sendPurchaseEvent(summaryData, productData, customerData);
    }
    scrollToTop();
  }, [step]);

  const showBreadCrumbs = step !== CheckoutStep.FINISHED && step !== CheckoutStep.CARTERRORS;

  return (
    <>
      {step !== CheckoutStep.FINISHED && (
        <div
          className={styles.backButton}
          onClick={() => {
            setIsIntoCheckout(false);
            updateUrlParams("cart");
          }}
        >
          <ArrowLeft className={styles.arrowleftIcon} />
          <span>{t("Zurück zum Warenkorb")}</span>
        </div>
      )}
      {showBreadCrumbs && <Breadcrumb />}
      {step === CheckoutStep.CARTERRORS && (
        <CheckoutError>{t("Es befinden sich keine oder fehlerhafte Artikel in Ihrem Warenkorb!")}</CheckoutError>
      )}
      <Login isVisible={step === CheckoutStep.LOGIN} />
      <AddressStep isVisible={step === CheckoutStep.ADDRESS} />
      <Shipping isVisible={step === CheckoutStep.SHIPPING} />
      <Payment isVisible={step === CheckoutStep.PAYMENT} />
      {step === CheckoutStep.SUMMARY && <Summary isVisible={step === CheckoutStep.SUMMARY} />}
      <Finished isVisible={step === CheckoutStep.FINISHED} />
    </>
  );
};
export default Checkout;

export const CheckoutError: React.FC = (props) => {
  const listOfErrors = React.Children.map(props.children, (child: string | JSX.Element) => {
    return !child ? undefined : <li>{child}</li>;
  });
  if (!(listOfErrors && listOfErrors.length)) {
    return null;
  }

  return (
    <div className="error-block-main">
      <ul>{listOfErrors}</ul>
    </div>
  );
};

export const StringWithAnchorComp: React.FC<{message: string}> = ({message}) => {
  const {setIsIntoCheckout} = useCartContext();
  const {t} = useTranslation("checkout");

  const backToCart = (evt: React.MouseEvent<HTMLAnchorElement>) => {
    evt.preventDefault();
    window.isBillingAgreementAddressSet = false;
    window.isBillingAgreementPaymentSet = false;
    setIsIntoCheckout(false);

    return false;
  };

  return (
    <React.Fragment>
      {t(message) + " "}
      <a href="#" onClick={backToCart}>
        {t("Zum Warenkorb", {ns: "cart"})}
      </a>
    </React.Fragment>
  );
};
export enum CheckoutStep {
  CARTERRORS = "carterrors",
  LOGIN = "login",
  ADDRESS = "address",
  SHIPPING = "shipping",
  PAYMENT = "payment",
  SUMMARY = "summary",
  FINISHED = "finished",
}
export enum LoginMode {
  NEWCUSTOMER = "new",
  KNOWNCUSTOMER = "known",
  FACEBOOK = "facebook",
}
export enum PaymentMethod {
  PAYPAL = "PayPal",
  AMAZONPAY = "AmazonPay",
  GOOGLEPAY = "GooglePay",
  APPLEPAY = "ApplePay",
}
export interface IIsVisible {
  isVisible: boolean;
}

export const scrollToTop = (): void => {
  const sections = document.querySelectorAll("#ModalRootElement > section");
  if (sections.length) {
    sections.forEach((section) => (section.scrollTop = 0));
  }
};

export const DEFAULT_ERROR_MESSAGE = "default error message";
export const NO_DETAILS_CONTEXT = {context: "nodetails"};
